import Phaser from "phaser";
import { getMyCreatures, startCombat, healAll } from "../api";
import {
  VIEWPORT, preloadUI, addBackground, addMusicBar, addTopButton,
  addActionButton, addEnterButton, addReturnButton, addDpad, addPetSlots,
  createHPBar, createXPBar, createViewportMask,
  type ImageButton, type ProgressBar, type PetSlotView,
} from "../ui";

const TILE = 20;
const COLS = 15;
const ROWS = 15;

// 0=grass, 1=path, 2=tree(wall), 3=entrance
// prettier-ignore
const MAP_DATA: number[][] = [
  [2,2,2,2,2,2,2,2,2,2,2,2,2,2,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,1,1,1,1,1,0,0,0,0,2],
  [2,0,0,0,0,1,0,0,0,1,0,0,0,0,2],
  [2,0,0,0,0,1,0,0,0,1,0,0,0,0,2],
  [2,0,0,1,1,1,0,0,0,1,1,1,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,0,0,0,0,0,0,0,2],
  [2,0,0,0,0,0,0,3,0,0,0,0,0,0,2],
  [2,2,2,2,2,2,2,2,2,2,2,2,2,2,2],
];

const TILE_COLORS: Record<number, number> = {
  0: 0x4a8c3f,
  1: 0xc4a76c,
  2: 0x2d5a1e,
  3: 0x8b6914,
};

export class MapScene extends Phaser.Scene {
  private playerCol = 7;
  private playerRow = 12;
  private playerRect!: Phaser.GameObjects.Rectangle;
  private mapContainer!: Phaser.GameObjects.Container;
  private stepsWithout = 0;
  private creatures: any[] = [];
  private activeIdx = 0;
  private moving = false;
  private keys!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasd!: Record<string, Phaser.Input.Keyboard.Key>;

  private hpBar!: ProgressBar;
  private xpBar!: ProgressBar;
  private petSlots!: PetSlotView[];
  private actionButtons: ImageButton[] = [];

  constructor() {
    super({ key: "MapScene" });
  }

  preload(): void {
    preloadUI(this);
  }

  async create(): Promise<void> {
    this.stepsWithout = 0;
    this.moving = false;
    this.actionButtons = [];

    // Fondo del marco
    addBackground(this);
    addMusicBar(this);

    // Top buttons
    addTopButton(this, "map", () => { /* TODO */ });
    addTopButton(this, "inventory", () => { /* TODO */ });
    addTopButton(this, "options", () => { /* TODO */ });

    // Viewport: contenedor con mask para el mapa
    this.mapContainer = this.add.container(0, 0);
    this.mapContainer.setMask(createViewportMask(this));

    this.buildMap();

    // HP/XP bars (muestran la criatura activa)
    this.hpBar = createHPBar(this);
    this.xpBar = createXPBar(this);

    // ENTER / RETURN
    addEnterButton(this, "CURAR", async () => {
      await healAll();
      await this.loadCreatures();
    });
    addReturnButton(this, "MENU", () => { /* TODO */ });

    // D-pad
    addDpad(this, (dir) => {
      const dirs = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
      const [dx, dy] = dirs[dir];
      if (!this.moving) this.tryMove(dx, dy);
    });

    // Action buttons (4 slots) – por ahora: Cambiar criatura (+/-)
    this.actionButtons.push(
      addActionButton(this, 1, "CAMBIAR", () => this.cycleCreature()),
      addActionButton(this, 2, "CURAR", async () => { await healAll(); await this.loadCreatures(); }),
      addActionButton(this, 3, "—", () => {}),
      addActionButton(this, 4, "—", () => {}),
    );

    // Pet slots (3x2). Los primeros 3 son las criaturas del jugador.
    this.petSlots = addPetSlots(this, (i) => {
      if (i < this.creatures.length) {
        this.activeIdx = i;
        this.updateHUD();
      }
    });

    // Keyboard
    if (this.input.keyboard) {
      this.keys = this.input.keyboard.createCursorKeys();
      this.wasd = {
        W: this.input.keyboard.addKey("W"),
        A: this.input.keyboard.addKey("A"),
        S: this.input.keyboard.addKey("S"),
        D: this.input.keyboard.addKey("D"),
      };
    }

    await this.loadCreatures();
  }

  private buildMap(): void {
    // El mapa 15x15 con TILE=20 = 300x300 → más grande que el viewport (316x240)
    // Centramos el mapa en el viewport horizontalmente y dejamos scroll vertical si hace falta.
    const mapW = COLS * TILE;
    const mapH = ROWS * TILE;
    const startX = VIEWPORT.x + (VIEWPORT.w - mapW) / 2;  // centrar horiz
    const startY = VIEWPORT.y + (VIEWPORT.h - mapH) / 2;  // centrar vert (negativo si map > viewport)

    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const tile = MAP_DATA[r][c];
        const rect = this.add.rectangle(
          startX + c * TILE + TILE / 2,
          startY + r * TILE + TILE / 2,
          TILE - 1, TILE - 1,
          TILE_COLORS[tile] ?? 0x333333,
        );
        this.mapContainer.add(rect);
      }
    }

    // Player (red square)
    this.playerRect = this.add.rectangle(
      startX + this.playerCol * TILE + TILE / 2,
      startY + this.playerRow * TILE + TILE / 2,
      TILE - 4, TILE - 4, 0xe74c3c,
    );
    this.mapContainer.add(this.playerRect);

    // Centrar cámara en el jugador dentro del viewport
    this.updateCamera();
  }

  private updateCamera(): void {
    // Scrollear el contenedor para mantener al jugador centrado en el viewport
    const mapW = COLS * TILE;
    const mapH = ROWS * TILE;
    const startX = VIEWPORT.x + (VIEWPORT.w - mapW) / 2;
    const startY = VIEWPORT.y + (VIEWPORT.h - mapH) / 2;

    // Posición absoluta del jugador
    const playerX = startX + this.playerCol * TILE + TILE / 2;
    const playerY = startY + this.playerRow * TILE + TILE / 2;

    // Queremos que el jugador quede cerca del centro del viewport
    const targetX = VIEWPORT.x + VIEWPORT.w / 2 - playerX;
    const targetY = VIEWPORT.y + VIEWPORT.h / 2 - playerY;

    // Clamp para que el mapa no muestre más allá de sus bordes
    const minX = VIEWPORT.x + VIEWPORT.w - (startX + mapW);
    const maxX = VIEWPORT.x - startX;
    const minY = VIEWPORT.y + VIEWPORT.h - (startY + mapH);
    const maxY = VIEWPORT.y - startY;

    this.mapContainer.x = Phaser.Math.Clamp(targetX, Math.min(minX, maxX), Math.max(minX, maxX));
    this.mapContainer.y = Phaser.Math.Clamp(targetY, Math.min(minY, maxY), Math.max(minY, maxY));
  }

  private async loadCreatures(): Promise<void> {
    try {
      const res = await getMyCreatures();
      this.creatures = res.creatures;
      const aliveIdx = this.creatures.findIndex((c: any) => c.current_hp > 0);
      if (aliveIdx >= 0) this.activeIdx = aliveIdx;

      // Preload sprites de todas las criaturas para los slots
      let needsLoad = false;
      for (const c of this.creatures) {
        const key = `creature_${c.sprite_key}`;
        if (c.sprite_url && !this.textures.exists(key)) {
          this.load.image(key, c.sprite_url);
          needsLoad = true;
        }
      }
      if (needsLoad) {
        this.load.once("complete", () => this.updateHUD());
        this.load.start();
      } else {
        this.updateHUD();
      }
    } catch { /* ignore */ }
  }

  private updateHUD(): void {
    const c = this.creatures[this.activeIdx];
    if (c) {
      this.hpBar.setProgress(c.current_hp, c.max_hp, `${c.current_hp}/${c.max_hp}`);
      this.xpBar.setProgress(c.xp, c.xp_next, `Nv.${c.level}  ${c.xp}/${c.xp_next}`);
    } else {
      this.hpBar.setProgress(0, 1, "—");
      this.xpBar.setProgress(0, 1, "—");
    }

    // Pet slots: primeras 3 posiciones = criaturas del jugador
    for (let i = 0; i < 6; i++) {
      const creature = i < this.creatures.length ? this.creatures[i] : null;
      this.petSlots[i].update(creature, i === this.activeIdx);
    }
  }

  private cycleCreature(): void {
    if (this.creatures.length === 0) return;
    this.activeIdx = (this.activeIdx + 1) % this.creatures.length;
    this.updateHUD();
  }

  update(): void {
    if (this.moving || !this.input.keyboard) return;
    let dx = 0, dy = 0;
    if (Phaser.Input.Keyboard.JustDown(this.keys.up) || Phaser.Input.Keyboard.JustDown(this.wasd.W)) dy = -1;
    else if (Phaser.Input.Keyboard.JustDown(this.keys.down) || Phaser.Input.Keyboard.JustDown(this.wasd.S)) dy = 1;
    else if (Phaser.Input.Keyboard.JustDown(this.keys.left) || Phaser.Input.Keyboard.JustDown(this.wasd.A)) dx = -1;
    else if (Phaser.Input.Keyboard.JustDown(this.keys.right) || Phaser.Input.Keyboard.JustDown(this.wasd.D)) dx = 1;
    if (dx !== 0 || dy !== 0) this.tryMove(dx, dy);
  }

  private tryMove(dx: number, dy: number): void {
    const newCol = this.playerCol + dx;
    const newRow = this.playerRow + dy;
    if (newRow < 0 || newRow >= ROWS || newCol < 0 || newCol >= COLS) return;
    const tile = MAP_DATA[newRow][newCol];
    if (tile === 2) return;

    this.moving = true;
    this.playerCol = newCol;
    this.playerRow = newRow;

    const mapW = COLS * TILE;
    const mapH = ROWS * TILE;
    const startX = VIEWPORT.x + (VIEWPORT.w - mapW) / 2;
    const startY = VIEWPORT.y + (VIEWPORT.h - mapH) / 2;

    this.tweens.add({
      targets: this.playerRect,
      x: startX + newCol * TILE + TILE / 2,
      y: startY + newRow * TILE + TILE / 2,
      duration: 100,
      onUpdate: () => this.updateCamera(),
      onComplete: () => {
        this.moving = false;
        this.updateCamera();
        if (tile === 0) this.checkEncounter();
      },
    });
  }

  private async checkEncounter(): Promise<void> {
    this.stepsWithout++;
    const chance = 0.05 + this.stepsWithout * 0.05;
    if (Math.random() >= chance) return;
    this.stepsWithout = 0;

    const active = this.creatures[this.activeIdx];
    if (!active || active.current_hp <= 0) {
      const aliveIdx = this.creatures.findIndex((c: any) => c.current_hp > 0);
      if (aliveIdx < 0) return;
      this.activeIdx = aliveIdx;
    }

    try {
      const combat = await startCombat(this.creatures[this.activeIdx].id);
      this.scene.start("CombatScene", {
        combatId: combat.combat_id,
        playerCreature: combat.player_creature,
        wildCreature: combat.wild_creature,
        allCreatures: this.creatures,
      });
    } catch { /* ignore */ }
  }
}
